// Branch-scoped permission cache.
//
// Stores the payload of `GET /guardian-users/permission` for the branch the
// user picked. The record keeps the branch it belongs to so a payload left
// over from a previous branch can be recognised (and ignored) instead of
// being mistaken for the current one.
//
//   {
//     branchId: 1,
//     type: 'COMPANY' | 'USER',
//     permissions: [{ id, companyId, branchId, moduleId, permission, moduleName, moduleSlug }],
//     fetchedAt: 1793272582000,
//   }
//
// The endpoint returns COMPANY permissions as a flat array, but nests USER
// permissions under roles[].permissions. Both are stored as the same flat list.
//
// The payload is cached only — the permission matcher
// (`src/utils/permission/index.js`) still reads the `/me` payload.

import { BRANCH_PERMISSIONS_STORAGE_KEY } from './constants'

// Pull the permission rows out of whatever shape the
// `GET /guardian-users/permission` endpoint returned.
//
// Shapes seen in the wild:
//
//   { type: 'COMPANY', permissions: [row, ...] }
//   { type: 'USER', roles: [{ id, permissions: [row, ...] }] }
//   { type: 'USER', permissions: [], roles: [{ id, permissions: [row, ...] }] }
//
// The third shape is the trap: the top-level `permissions` array exists but
// is EMPTY, while the real rows live under `roles[].permissions`. Picking
// the flat list unconditionally would cache an empty permission set and
// hide every module — including the ones the user's role does grant.
//
// Rows are merged (flat first, then role rows) rather than one-or-the-other,
// so a payload that ships both keeps everything.
function flattenPermissionRows(payload) {
  const rows = []

  const pushRows = (list) => {
    if (!Array.isArray(list)) return
    list.forEach((row) => {
      if (row && typeof row === 'object') rows.push(row)
    })
  }

  pushRows(payload?.permissions)

  const roles = payload?.roles
  if (Array.isArray(roles)) {
    roles.forEach((role) => pushRows(role?.permissions))
  } else if (roles && typeof roles === 'object') {
    // Organised shape — `{ [roleId]: { permissions: [...] } }`.
    Object.values(roles).forEach((role) => pushRows(role?.permissions))
  }

  return rows
}

// The permission matcher runs on every render of every gated control, so the
// parsed payload is memoised against the raw string it came from. A write
// replaces the raw string, which invalidates the cache automatically.
let cachedRaw = null
let cachedRecord = null

export const permissionStorage = {
  getPermissions: () => {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(BRANCH_PERMISSIONS_STORAGE_KEY)
    if (raw === cachedRaw) return cachedRecord
    cachedRaw = raw
    if (!raw) {
      cachedRecord = null
      return null
    }
    try {
      const parsed = JSON.parse(raw)
      cachedRecord = parsed && typeof parsed === 'object' ? parsed : null
    } catch {
      cachedRecord = null
    }
    return cachedRecord
  },

  setPermissions: (payload, branchId) => {
    if (typeof localStorage === 'undefined') return null
    if (!payload) {
      permissionStorage.clearPermissions()
      return null
    }
    const permissions = flattenPermissionRows(payload)
    const record = {
      branchId: branchId ?? payload.branchId ?? null,
      type: payload.type ?? null,
      permissions,
      fetchedAt: Date.now(),
    }
    localStorage.setItem(BRANCH_PERMISSIONS_STORAGE_KEY, JSON.stringify(record))
    return record
  },

  // The cached payload only counts when it belongs to the given branch.
  getPermissionsForBranch: (branchId) => {
    const record = permissionStorage.getPermissions()
    if (!record) return null
    if (branchId === undefined || branchId === null || branchId === '') return record
    if (record.branchId === null || record.branchId === undefined) return null
    return String(record.branchId) === String(branchId) ? record : null
  },

  clearPermissions: () => {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(BRANCH_PERMISSIONS_STORAGE_KEY)
  },
}
